import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import {
    ApiPayload,
    GetPostDislikeReasonsResponse,
    GetPostSummaryResponse,
    GetBiddingPostsApiResponse,
    GetBiddingPostsApiParams,
    PostAttachments,
    PostInfo,
    PostLocation,
    PostOwner,
    PostPaymentInfo,
} from '../../types/biddingPosts';


// ─── Service ─────────────────────────────────────────────────────────────────

export const biddingPostsService = createApi({
    reducerPath: 'biddingPostsService',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['PostDislikeReasons', 'PostSummary', 'BiddingPosts', 'PostDetails'],
    endpoints: (builder) => ({
        getPostDislikeReasons: builder.query<
            GetPostDislikeReasonsResponse,
            { postId: string }
        >({
            query: ({ postId }) => ({
                url: `/aggregation-service/v1/operator/posts/${postId}/dislike-reasons`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { postId }) => [
                { type: 'PostDislikeReasons', id: postId },
            ],
        }),

        getPostSummary: builder.query<
            GetPostSummaryResponse,
            { postId: string }
        >({
            query: ({ postId }) => ({
                url: `/supply-service/v1/operator/posts/${postId}/summary`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { postId }) => [
                { type: 'PostSummary', id: postId },
            ],
        }),

        getBiddingPosts: builder.query<
            GetBiddingPostsApiResponse,
            GetBiddingPostsApiParams
        >({
            query: ({ page = 0, size = 10, search, status, dateFrom, dateTo, disliked }) => ({
                url: '/aggregation-service/v1/operator/posts',
                method: 'GET',
                params: {
                    page,
                    size,
                    ...(search ? { search } : {}),
                    ...(status?.length ? { status } : {}),
                    ...(dateFrom ? { dateFrom } : {}),
                    ...(dateTo ? { dateTo } : {}),
                    ...(disliked !== undefined ? { disliked } : {}),
                },
            }),
            providesTags: (result) =>
                result
                    ? [
                        ...result.payload.content.map(({ postId }) => ({
                            type: 'BiddingPosts' as const,
                            id: postId,
                        })),
                        { type: 'BiddingPosts', id: 'LIST' },
                    ]
                    : [{ type: 'BiddingPosts', id: 'LIST' }],
        }),

        getPostInfo: builder.query<ApiPayload<PostInfo>, { postId: string }>({
            query: ({ postId }) => ({
                url: `/supply-service/v1/operator/posts/${postId}/info`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { postId }) => [{ type: 'PostDetails', id: postId }],
        }),

        getPostPaymentInfo: builder.query<ApiPayload<PostPaymentInfo>, { postId: string }>({
            query: ({ postId }) => ({
                url: `/supply-service/v1/posts/${postId}/payment-info`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { postId }) => [{ type: 'PostDetails', id: postId }],
        }),

        getPostLocations: builder.query<ApiPayload<PostLocation[]>, { postId: string }>({
            query: ({ postId }) => ({
                url: `/supply-service/v1/posts/${postId}/locations`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { postId }) => [{ type: 'PostDetails', id: postId }],
        }),

        getPostAttachments: builder.query<ApiPayload<PostAttachments>, { postId: string }>({
            query: ({ postId }) => ({
                url: `/supply-service/v1/posts/${postId}/attachments`,
                method: 'GET',
                params: {
                    includeEvaluationDocs: true,
                },
            }),
            providesTags: (_result, _error, { postId }) => [{ type: 'PostDetails', id: postId }],
        }),

        getPostOwner: builder.query<ApiPayload<PostOwner>, { postId: string }>({
            query: ({ postId }) => ({
                url: `/aggregation-service/v1/posts/${postId}/owner`,
                method: 'GET',
            }),
            providesTags: (_result, _error, { postId }) => [{ type: 'PostDetails', id: postId }],
        }),

        deletePost: builder.mutation<void, { postId: string }>({
            query: ({ postId }) => ({
                url: `/supply-service/v1/operator/posts/${postId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { postId }) => [
                { type: 'BiddingPosts', id: 'LIST' },
                { type: 'BiddingPosts', id: postId },
            ],
        }),
    }),
});

export const {
    useGetPostDislikeReasonsQuery,
    useGetPostSummaryQuery,
    useGetBiddingPostsQuery,
    useGetPostInfoQuery,
    useGetPostPaymentInfoQuery,
    useGetPostLocationsQuery,
    useGetPostAttachmentsQuery,
    useGetPostOwnerQuery,
    useDeletePostMutation,
} = biddingPostsService;
